import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type EnrollmentWizardStep = "basic" | "abm" | "setup" | "support" | "devices" | "review";

interface Step {
  id: EnrollmentWizardStep;
  name: string;
  description: string;
}

const steps: Step[] = [
  {
    id: "basic",
    name: "Basic Info",
    description: "Name and description"
  },
  {
    id: "abm", 
    name: "ABM Configuration",
    description: "Select ABM account"
  },
  {
    id: "setup",
    name: "Setup Assistant",
    description: "Configure enrollment options"
  },
  {
    id: "support",
    name: "Support Info",
    description: "Contact information"
  },
  {
    id: "devices",
    name: "Device Assignment",
    description: "Assign devices"
  },
  {
    id: "review",
    name: "Review & Create",
    description: "Review and deploy"
  }
];

interface EnrollmentWizardStepsProps {
  currentStep: EnrollmentWizardStep;
  completedSteps: EnrollmentWizardStep[];
}

export const EnrollmentWizardSteps = ({ currentStep, completedSteps }: EnrollmentWizardStepsProps) => {
  const currentStepIndex = steps.findIndex(step => step.id === currentStep);

  return (
    <div className="border-b bg-muted/50">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {steps.map((step, stepIdx) => {
              const isCompleted = completedSteps.includes(step.id);
              const isCurrent = step.id === currentStep;
              const isUpcoming = stepIdx > currentStepIndex;

              return (
                <li key={step.id} className={cn("relative", stepIdx !== steps.length - 1 && "pr-4 sm:pr-12")}>
                  <div className="flex items-center">
                    <div className="relative flex h-8 w-8 items-center justify-center">
                      {isCompleted ? (
                        <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                          <Check className="h-5 w-5 text-primary-foreground" />
                        </div>
                      ) : isCurrent ? (
                        <div className="h-8 w-8 rounded-full border-2 border-primary bg-background flex items-center justify-center">
                          <div className="h-3 w-3 rounded-full bg-primary" />
                        </div>
                      ) : (
                        <div className="h-8 w-8 rounded-full border-2 border-muted-foreground/30 bg-background flex items-center justify-center">
                          <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                        </div>
                      )}
                    </div>
                    <div className="ml-3 min-w-0 flex-1 hidden sm:block">
                      <p className={cn(
                        "text-sm font-medium leading-tight",
                        isCurrent 
                          ? "text-primary" 
                          : isCompleted 
                            ? "text-foreground" 
                            : "text-muted-foreground"
                      )}>
                        {step.name}
                      </p>
                      <p className="text-xs text-muted-foreground leading-tight truncate">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  {stepIdx !== steps.length - 1 && (
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className="h-0.5 w-full bg-muted-foreground/20 ml-10" />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </div>
  );
};
